import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { HomeWidget } from '@/components/home-widgets/HomeWidget';
import {
  HOME_WIDGET_KINDS,
  HOME_WIDGET_SIZES,
  type HomeWidgetKind,
  type HomeWidgetStateKind,
  type WidgetSize,
} from '@/components/home-widgets/homeWidgetData';
import {
  DEFAULT_HOME_SETTINGS,
  HOME_WIDGET_CATALOG,
  HOME_WIDGET_TYPE_TITLES,
  sampleHomeWidgetState,
} from '@/components/home-widgets/homeWidgetSamples';
import { Code, Section } from './Section';
const now = Date.parse('2026-10-26T12:00:00Z');
function TypeTile({ kind }: { kind: HomeWidgetKind }) {
  const [state, setState] = useState<HomeWidgetStateKind>('ready'),
    [size, setSize] = useState<WidgetSize>(HOME_WIDGET_SIZES[kind][0]),
    [chart, setChart] = useState('income-chart');
  const source = HOME_WIDGET_CATALOG.find(
    (definition) => definition.type === kind && (kind !== 'chart' || definition.id === chart),
  )!;
  const definition = { ...source, allowedSizes: HOME_WIDGET_SIZES[kind] };
  const snapshot =
    state === 'ready'
      ? sampleHomeWidgetState(definition, DEFAULT_HOME_SETTINGS, now)
      : state === 'error'
        ? { state: 'error' as const, message: 'Не получилось обновить' }
        : { state };
  return (
    <div data-home-type-tile={kind} className="min-w-0 space-y-4 rounded-lg border border-line bg-canvas p-4">
      <h3 className="type-heading">{HOME_WIDGET_TYPE_TITLES[kind]}</h3>
      <div className="flex flex-wrap gap-3">
        <div className="w-32 space-y-1">
          <label id={'home-type-' + kind + '-size'} className="text-2xs text-fg-muted">
            Размер
          </label>
          <Select
            value={size.columns + 'x' + size.rows}
            onValueChange={(value) => {
              const next = HOME_WIDGET_SIZES[kind].find((size) => size.columns + 'x' + size.rows === value);
              if (next) setSize(next);
            }}
          >
            <SelectTrigger aria-labelledby={'home-type-' + kind + '-size'}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOME_WIDGET_SIZES[kind].map((size) => (
                <SelectItem key={size.columns + 'x' + size.rows} value={size.columns + 'x' + size.rows}>
                  {size.columns} × {size.rows}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40 space-y-1">
          <label id={'home-type-' + kind + '-state'} className="text-2xs text-fg-muted">
            Состояние
          </label>
          <Select value={state} onValueChange={(value) => setState(value as HomeWidgetStateKind)}>
            <SelectTrigger aria-labelledby={'home-type-' + kind + '-state'}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ready">Данные</SelectItem>
              <SelectItem value="loading">Загрузка</SelectItem>
              <SelectItem value="error">Ошибка</SelectItem>
              <SelectItem value="no-access">Нет доступа</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {kind === 'chart' && (
          <div className="w-40 space-y-1">
            <label id="home-type-chart-variant" className="text-2xs text-fg-muted">
              Вид
            </label>
            <Select value={chart} onValueChange={setChart}>
              <SelectTrigger aria-labelledby="home-type-chart-variant">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="income-chart">Столбцы</SelectItem>
                <SelectItem value="load-chart">Линия</SelectItem>
                <SelectItem value="density-chart">Тепловая карта</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div data-home-size-preview="" className="max-h-96 max-w-full overflow-auto rounded-lg">
        <div
          style={{
            width:
              'calc(' +
              size.columns +
              ' * var(--spacing-24) + ' +
              (size.columns - 1) +
              ' * var(--spacing-4))',
            height:
              'calc(' + size.rows + ' * var(--spacing-48) + ' + (size.rows - 1) + ' * var(--spacing-4))',
          }}
        >
          <HomeWidget
            definition={definition}
            instance={{
              instanceId: 'tile-' + kind,
              definitionId: definition.id,
              size,
              settings: DEFAULT_HOME_SETTINGS,
            }}
            state={snapshot}
            now={now}
            onRetry={() => setState('ready')}
          />
        </div>
      </div>
      <Code>
        HomeWidget · {kind} · {size.columns} × {size.rows}
      </Code>
    </div>
  );
}
export function HomeWidgetTypesSection() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== '#home-widget-types') return;
    let active = true;
    const align = () => {
      if (active)
        document.getElementById('home-widget-types')?.scrollIntoView({ block: 'start', behavior: 'instant' });
    };
    const frame = requestAnimationFrame(align);
    void document.fonts.ready.then(align);
    return () => {
      active = false;
      cancelAnimationFrame(frame);
    };
  }, [hash]);
  return (
    <Section
      id="home-widget-types"
      title="Виджеты Главной"
      lead="Девять общих типов, одна рамка. Размеры и состояния переключаются в каждой плитке; точная раскладка — в образце Главной."
    >
      <div className="grid min-w-0 items-start gap-6 xl:grid-cols-2">
        {HOME_WIDGET_KINDS.map((kind) => (
          <TypeTile key={kind} kind={kind} />
        ))}
      </div>
      <Link
        to="/design/home?catalog=1"
        className="inline-flex w-fit items-center gap-2 rounded-sm text-sm font-medium text-brand-fg hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        Открыть Главную и каталог
        <ArrowRight className="size-4" />
      </Link>
    </Section>
  );
}
